import axios from './axiosInstance';
import { APIFeature } from '../../types';

interface SportAreaParams {
  sport: string;
  areaId: string;
}

interface GetFeatureParams extends SportAreaParams {
  featureId: string;
}

interface GetFeaturesParams extends SportAreaParams {
  trailId?: string;
}

interface GetHistoricalFeaturesParams extends SportAreaParams {
  timestamp: number;
}

interface GetCsvParams extends SportAreaParams {
  timestamp: number;
  fileName: string;
}

interface CreateFeatureParams extends SportAreaParams {
  type: number;
  title: string;
  latitude: number;
  longitude: number;
  size?: string;
  description?: string;
  terrainParkId?: string;
  terrainParkName?: string;
  trailIds?: string[];
  /** Optional idempotency key — the PUT create route runs the backend's
   *  IdempotentRequests middleware, keyed on the x-request-id header. */
  requestId?: string;
}

interface PostFeaturePhotoParams extends SportAreaParams {
  featureId: string;
  /** Raw base64 JPEG/PNG (no data: prefix), under the API Gateway cap. */
  base64File: string;
  /** Optional idempotency key (same middleware as the create route). */
  requestId?: string;
}

interface UpdateFeatureParams extends SportAreaParams {
  featureId: string;
  timestamp?: number;
  type?: number;
  status?: number;
  size?: string;
  title?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  terrainParkName?: string;
  terrainParkId?: string;
  trailIds?: string[];
}

interface UpdateMultipleFeaturesParams extends SportAreaParams {
  features: Partial<APIFeature>[];
}

interface UpdateLinksParams extends SportAreaParams {
  features: Partial<APIFeature>[];
}

const getFeature = ({ sport, areaId, featureId }: GetFeatureParams): Promise<APIFeature | void> => {
  let url = `/${sport}/areas/${areaId}/features/${featureId}`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
    });
};

const getFeatures = ({ sport, areaId, trailId }: GetFeaturesParams): Promise<APIFeature[]> => {
  let url = `/${sport}/areas/${areaId}/features?newImageUrlScheme=true`;
  if (trailId) {
    url += `&trailId=${trailId}`
  }
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getHistoricalFeatures = ({ sport, areaId, timestamp }: GetHistoricalFeaturesParams): Promise<APIFeature[]> => {
  let url = `/${sport}/areas/${areaId}/snapshots?time=${timestamp}&newImageUrlScheme=true`;
  return axios.get(url)
    .then((response) => {
      return response.data && response.data.features;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getCsv = ({ sport, areaId, timestamp, fileName }: GetCsvParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/exportsnapshots`;
  return axios.post(url, {
    timestamps: [timestamp],
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.csv`); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .catch((error) => {
      console.log(error);
    });
};

// PUT — create a park feature (ullr-139 Phase 6). Geometry is a single drawn
// point → latitude/longitude (features are point-only). The backend create path
// takes no status (a new feature defaults server-side), so it's omitted here.
// Returns the new feature.
const createFeature = ({
  sport,
  areaId,
  type,
  title,
  latitude,
  longitude,
  size,
  description,
  terrainParkId,
  terrainParkName,
  trailIds,
  requestId,
}: CreateFeatureParams): Promise<APIFeature> => {
  const url = `/${sport}/areas/${areaId}/features`;
  return axios.put(url, {
    type: Number(type),
    title,
    latitude,
    longitude,
    size,
    description,
    terrainParkId,
    terrainParkName,
    trailIds,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

// POST — attach a photo to a feature (ullr-140 Phase 7 import flow; same
// endpoint UllrMobile's postFeaturePhoto uses). Body is the resized base64.
const postFeaturePhoto = ({
  sport,
  areaId,
  featureId,
  base64File,
  requestId,
}: PostFeaturePhotoParams): Promise<any> => {
  const url = `/${sport}/areas/${areaId}/features/${featureId}/photos`;
  return axios.post(url, {
    base64File,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const updateFeature = ({
  sport,
  areaId,
  featureId,
  timestamp,
  type,
  status,
  size,
  title,
  description,
  latitude,
  longitude,
  terrainParkName,
  terrainParkId,
  trailIds,
}: UpdateFeatureParams): Promise<APIFeature> => {
  let url = `/${sport}/areas/${areaId}/features/${featureId}`;
  return axios.post(url, {
    timestamp,
    title: title || '',
    description: description || '',
    type: Number(type),
    status: status,
    size,
    latitude,
    longitude,
    terrainParkName,
    terrainParkId,
    trailIds,
  })
    .then((response) => {
      const feature = response.data;
      return feature;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const updateMultipleFeatures = ({
  sport,
  areaId,
  features,
}: UpdateMultipleFeaturesParams): Promise<APIFeature[]> => {
  let url = `/${sport}/areas/${areaId}/features`;
  return axios.post(url, features)
    .then((response) => {
      const features = response.data;
      return features;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const updateLinks = ({
  sport,
  areaId,
  features,
}: UpdateLinksParams): Promise<APIFeature[]> => {
  let url = `/${sport}/areas/${areaId}/features/links`;
  return axios.post(url, features)
    .then((response) => {
      const features = response.data;
      return features;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const featureFunctions = {
  createFeature,
  postFeaturePhoto,
  getFeature,
  getFeatures,
  getHistoricalFeatures,
  getCsv,
  updateFeature,
  updateMultipleFeatures,
  updateLinks,
};

export default featureFunctions;
